import { useEffect, useMemo } from "react";

import type { AdoptionRateDetailsDto, AdoptionRateDetailsGraphItemDto } from "@/api/types";
import { useMetricHelp } from "@/components/Analytics/useMetricHelp";
import { AskNobiButton } from "@/components/AskNobi/AskNobiButton";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { NoData } from "@/components/NoData/NoData";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Column, Columns } from "@/design-system/Columns";
import { Inline as LayoutInline } from "@/design-system/Inline";
import { Notice } from "@/design-system/Notice";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { isDefined } from "@/helpers/util";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useScrollToPageTop } from "@/hooks/useScrollToPageTop";
import { useSlug } from "@/hooks/useSlug";
import { AnalyticsCard } from "@/modules/analytics/components/AnalyticsCard";
import { BarChart } from "@/modules/analytics/components/BarChart";
import type { DateRangeOption } from "@/modules/analytics/util";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";

export interface AdoptionAnalyticsDetailsProps {
  adoption: AdoptionRateDetailsDto | undefined;
  reach: AdoptionRateDetailsGraphItemDto[];
  vacant: AdoptionRateDetailsGraphItemDto[];
  isLoadingAdoptionData: boolean;
  isLoadingVacantData: boolean;
  isLoadingReachData: boolean;
  selectedDateRange: DateRangeOption;
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
  onDateRangeChange: (dateRange: DateRangeOption) => void;
}
export function AdoptionAnalyticsDetails({
  adoption,
  vacant,
  reach,
  isLoadingAdoptionData,
  isLoadingVacantData,
  isLoadingReachData,
  selectedDateRange,
  dateRangeOptions,
  onDateRangeChange,
}: AdoptionAnalyticsDetailsProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const help = useMetricHelp();
  const { project } = useSessionUser();
  const scrollToTop = useScrollToPageTop();
  useEffect(() => {
    scrollToTop("instant");
  }, [scrollToTop]);
  const reachDataPoints = useMemo(
    () =>
      reach?.map((point) => ({
        x: parseChartDate(point.range).toISOString(),
        y: point.percentage,
        isPending: point.isCurrentMonth,
      })) || [],
    [reach],
  );
  const adoptionDataPoints = useMemo(
    () =>
      adoption?.data?.map((point) => ({
        x: parseChartDate(point.range).toISOString(),
        y: point.percentage,
        isPending: point.isCurrentMonth,
      })) || [],
    [adoption],
  );
  const vacantDataPoints = useMemo(
    () =>
      vacant?.map((point) => ({
        x: parseChartDate(point.range).toISOString(),
        y: point.percentage,
      })) || [],
    [vacant],
  );
  const benchmark = useMemo(
    () =>
      adoption?.benchmark?.map((point) => ({
        x: parseChartDate(point.range).toISOString(),
        y: point.percentage,
      })) || [],
    [adoption],
  );
  const noReachDataAvailable = !reach;
  const noAdoptionDataAvailable = !adoption?.data || adoption.data.every((x) => x.percentage === 0);
  const noVacantDataAvailable = !vacant;

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.analytics-details.adoption.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.analytics-overview.overview.title"),
              to: routes.analytics.overview({
                slug,
              }),
            },
            {
              name: t("page.analytics-details.adoption.title"),
            },
          ]}
        />
      }
      actions={
        <AskNobiButton
          defaultFilters={{ projectIds: [project.id] }}
          context={{
            scope: "project",
            entity: "analytics adoption",
            page: "detail",
          }}
          suggestions={[
            t("component.ask-nobi-chat-drawer.analytics-adoption.suggestion.1"),
            t("component.ask-nobi-chat-drawer.analytics-adoption.suggestion.2"),
          ]}
        />
      }
      header={
        <LayoutInline gap="sm" align="center">
          <span className="text-body-emphasis">{t("page.analytics-details.time-range.label")}</span>
          <Select
            items={dateRangeOptions}
            selected={selectedDateRange}
            onChange={onDateRangeChange}
            renderOption={(x) => x.name}
            keySelector={(x) => x.value}
          />
        </LayoutInline>
      }
    >
      <Columns gap="md" collapseBelow="md">
        <Column width="two-thirds">
          <Stack gap="md">
            <AnalyticsCard.Root>
              <AnalyticsCard.Header
                title={t("page.analytics-details.adoption.chart.reach.title")}
                tooltip={help("page.analytics-details.adoption.chart.reach.title.tooltip")}
              />
              {isLoadingReachData && (
                <LayoutInline justify="center">
                  <Spinner size="xl" />
                </LayoutInline>
              )}
              {!isLoadingReachData && noReachDataAvailable && <NoData error={noReachDataAvailable} />}
              {!isLoadingReachData && !noReachDataAvailable && (
                <section className="flex flex-1 flex-col gap-space-md">
                  <span className="text-caption text-text-secondary">
                    {project.type === "addressBased"
                      ? t("page.analytics-details.adoption.reach.chart.adoption.description")
                      : t("page.analytics-details.adoption.reach.chart.company.description")}
                  </span>
                  <figure className="flex max-h-screen-minus-64 min-h-scroll-area-standard w-full flex-1 flex-col">
                    <BarChart
                      xAxisType="date"
                      dataPrimary={{
                        data: reachDataPoints,
                        color: dataColors.green,
                      }}
                      formatYTick={(y) => `${y}%`}
                      maxY={100}
                      hoverLabel={(data) => (
                        <Stack>
                          <span>
                            {t("page.analytics-details.engagement.chart.tooltip.primary", {
                              percentage: data.primary?.y || 0,
                            })}
                          </span>
                          {isDefined(data.benchmark) && (
                            <span>
                              {t("page.analytics-details.engagement.chart.tooltip.benchmark", {
                                percentage: data.benchmark.y || 0,
                              })}
                            </span>
                          )}
                          {data.primary?.isPending && (
                            <span>
                              {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                            </span>
                          )}
                        </Stack>
                      )}
                    />
                    <ChartLegend
                      items={[
                        {
                          label: t("page.analytics-details.engagement.chart.adoption.reach.legend.primary"),
                          color: dataColors.green,
                        },
                        {
                          label: t("page.analytics-details.engagement.chart.legend.current-period"),
                          color: dataColors.green,
                          pattern: "striped",
                        },
                      ]}
                    />
                  </figure>
                </section>
              )}
            </AnalyticsCard.Root>
            <AnalyticsCard.Root>
              <AnalyticsCard.Header
                title={
                  project.type === "addressBased"
                    ? t("page.analytics-details.adoption.chart.adoption.title")
                    : t("page.analytics-details.adoption.chart.company.title")
                }
                tooltip={help(
                  project.type === "addressBased"
                    ? "page.analytics-details.adoption.chart.adoption.title.tooltip"
                    : "page.analytics-details.adoption.chart.company.title.tooltip",
                )}
              />
              {isLoadingAdoptionData && (
                <LayoutInline justify="center">
                  <Spinner size="xl" />
                </LayoutInline>
              )}
              {!isLoadingAdoptionData && noAdoptionDataAvailable && <NoData error={noAdoptionDataAvailable} />}
              {!isLoadingAdoptionData && !noAdoptionDataAvailable && (
                <section className="flex flex-1 flex-col gap-space-md">
                  <span className="text-caption text-text-secondary">
                    {project.type === "addressBased"
                      ? t("page.analytics-details.adoption.chart.adoption.description")
                      : t("page.analytics-details.adoption.chart.company.description")}
                  </span>
                  <figure className="flex max-h-screen-minus-64 min-h-scroll-area-standard w-full flex-1 flex-col">
                    <BarChart
                      xAxisType="date"
                      dataPrimary={{
                        data: adoptionDataPoints,
                      }}
                      dataBenchmark={benchmark}
                      formatYTick={(y) => `${y}%`}
                      maxY={100}
                      hoverLabel={(data) => (
                        <Stack>
                          <span>
                            {t("page.analytics-details.engagement.chart.tooltip.primary", {
                              percentage: data.primary?.y || 0,
                            })}
                          </span>
                          {isDefined(data.benchmark) && (
                            <span>
                              {t("page.analytics-details.engagement.chart.tooltip.benchmark", {
                                percentage: data.benchmark.y || 0,
                              })}
                            </span>
                          )}
                          {data.primary?.isPending && (
                            <span>
                              {t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}
                            </span>
                          )}
                        </Stack>
                      )}
                    />
                    <ChartLegend
                      items={[
                        {
                          label: t("page.analytics-details.engagement.chart.adoption.legend.primary"),
                          color: dataColors.primary,
                        },
                        {
                          label: t("common.label.aop-benchmark"),
                          color: dataColors.benchmark,
                        },
                        {
                          label: t("page.analytics-details.engagement.chart.legend.current-period"),
                          color: dataColors.primary,
                          pattern: "striped",
                        },
                      ]}
                    />
                  </figure>
                  <Notice variant="special" message={t("page.analytics-details.adoption.chart.adoption.notice")} />
                </section>
              )}
            </AnalyticsCard.Root>
            {project.type === "addressBased" && (
              <AnalyticsCard.Root>
                <AnalyticsCard.Header
                  title={t("page.analytics-details.adoption.chart.vacant.title")}
                  tooltip={help("page.analytics-details.adoption.chart.vacant.title.tooltip")}
                />
                {isLoadingVacantData && (
                  <LayoutInline justify="center">
                    <Spinner size="xl" />
                  </LayoutInline>
                )}
                {!isLoadingVacantData && noVacantDataAvailable && <NoData error={noVacantDataAvailable} />}
                {!isLoadingVacantData && !noVacantDataAvailable && (
                  <section className="flex flex-1 flex-col gap-space-md">
                    <span className="text-caption text-text-secondary">
                      {t("page.analytics-details.adoption.chart.vacant.description")}
                    </span>
                    <figure className="flex max-h-screen-minus-64 min-h-scroll-area-standard w-full flex-1">
                      <BarChart
                        xAxisType="date"
                        dataPrimary={{
                          data: vacantDataPoints,
                        }}
                        maxY={100}
                        hoverLabel={(data) => (
                          <span className="whitespace-pre-line">
                            {t("page.analytics-details.adoption.chart.vacant.tooltip", {
                              percentage: data.primary?.y || 0,
                            })}
                          </span>
                        )}
                      />
                    </figure>
                  </section>
                )}
              </AnalyticsCard.Root>
            )}
          </Stack>
        </Column>
        <Column width="third">
          <Stack gap="md">
            <AnalyticsCard.Root height="content" contentAlignment="start">
              <AnalyticsCard.Header title={t("page.analytics-details.adoption.reach.information.title")} />
              <ul className="list-disc p-space-md">
                {t("page.analytics-details.adoption.reach.information.content")
                  .split("\n")
                  .map((item, i) => item.length > 0 && <li key={i}>{item}</li>)}
              </ul>
            </AnalyticsCard.Root>
            <AnalyticsCard.Root height="content" contentAlignment="start">
              <AnalyticsCard.Header title={t("page.analytics-details.adoption.information.title")} />
              <ul className="list-disc p-space-md">
                {t("page.analytics-details.adoption.information.content")
                  .split("\n")
                  .map((item, i) => item.length > 0 && <li key={i}>{item}</li>)}
              </ul>
            </AnalyticsCard.Root>
            <AnalyticsCard.Root height="content" contentAlignment="start">
              <AnalyticsCard.Header title={t("page.analytics-details.adoption.best-practices.title")} />
              <span className="text-caption-emphasis">
                {t("page.analytics-details.adoption.best-practices.subtitle")}
              </span>
              <ul className="list-disc p-space-md">
                {t("page.analytics-details.adoption.best-practices.content")
                  .split("\n")
                  .map((item, i) => item.length > 0 && <li key={i}>{item}</li>)}
              </ul>
            </AnalyticsCard.Root>
          </Stack>
        </Column>
      </Columns>
    </DocumentPage>
  );
}
